import { useOrdersStore } from "./use-orders-store";
import { Order } from "@/types/supabase";
import { toastHandler } from "@/lib/ToastHandler";
import { getOrderDetail } from "@/services/ordersClient";
import { API_ORDER_SERVICE_URL } from "@/lib/constants";
import { getAccessTokenDirect } from "@/lib/auth0";
import { io, Socket } from "socket.io-client";

// Socket.IO connection state
let socket: Socket | null = null;

let authToken: string | null = null;
function resolveSocketUrl() {
  try {
    const base = new URL(API_ORDER_SERVICE_URL);
    const httpProtocol = base.protocol === "https:" ? "https:" : "http:";
    // Server exposes Socket.IO at /ws/orders (namespace or path)
    return `${httpProtocol}//${base.host}/ws/orders`;
  } catch {
    return `${API_ORDER_SERVICE_URL}/ws/orders`;
  }
}
const SOCKET_URL = resolveSocketUrl();

// Maintain a Set of notified order IDs to avoid duplicate toasts
const notifiedOrderIds = new Set<number>();

export function isOrdersRealtimeActive() {
  return Boolean(socket && socket.connected);
}

function dispatchRealtimeEvent(type: "open" | "close") {
  window.dispatchEvent(new CustomEvent(`orders-realtime:${type}`));
}

// Dispatch granular update events for consumers (pages/components)
function dispatchRealtimeUpdate(detail: {
  type: string;
  orderId?: number;
  order?: Order | null;
}) {
  try {
    window.dispatchEvent(new CustomEvent("orders-realtime:update", { detail }));
  } catch (e) {
    console.warn("[orders-realtime] Failed to dispatch update event:", e);
  }
}

// Fetch a single order using our API client
async function fetchOrder(orderId: number): Promise<Order | null> {
  try {
    const resp = await getOrderDetail(orderId);
    const data = (resp as any)?.data || resp || null;
    return (data as Order) || null;
  } catch (error) {
    console.error("Error fetching order detail:", error);
    return null;
  }
}

// --- DEBUG: Below, we've added debug output to help surface WebSocket and state issues ---

export function setOrdersRealtimeToken(token: string | null) {
  authToken = token || null;
}

export function initOrdersRealtime(token?: string | null) {
  // Debug: Show current socket state before attempting connection
  console.debug("[orders-realtime] initOrdersRealtime called. Socket:", socket);

  if (socket && socket.connected) {
    console.debug(
      "[orders-realtime] Existing open socket, skipping new connect.",
    );
    return;
  }

  if (socket && !socket.disconnected) {
    console.debug(
      "[orders-realtime] Cleaning up lingering socket before reconnecting",
    );
    cleanupOrdersRealtime();
  }

  if (typeof token === "string") {
    authToken = token;
  }
  connectSocket();
}

async function connectSocket() {
  try {
    // Get token without React hooks (safe outside components)
    const token = authToken || (await getAccessTokenDirect().catch(() => null));
    console.debug("[orders-realtime] Preparing Socket.IO connect:", {
      url: SOCKET_URL,
      tokenPresent: Boolean(token),
      tokenPreview: token ? `${String(token).slice(0, 8)}…` : null,
    });

    // Create Socket.IO client
    socket = io(SOCKET_URL, {
      transports: ["websocket"],
      query: token ? { token } : undefined,
      autoConnect: true,
      reconnection: true,
    });

    socket.on("connect", () => {
      console.debug("[orders-realtime] Socket.IO connected", {
        id: socket?.id,
      });
      dispatchRealtimeEvent("open");
    });

    socket.on("connect_error", (err: any) => {
      console.error("[orders-realtime] connect_error", err?.message || err);
    });

    // Primary server event for order changes
    socket.on("order_event", async (msg: any) => {
      const store = useOrdersStore.getState();
      const { paginatedOrdersCurrentDay, currentPage, pageSize, fetchOptions } =
        store;

      console.debug("[orders-realtime] order_event:", msg);

      const eventType =
        msg?.eventType || msg?.event || msg?.type || msg?.action || "";

      const orderObj: any =
        msg?.order ||
        msg?.orderData ||
        msg?.data ||
        msg?.new ||
        msg?.payload?.order ||
        null;
      const orderId: number | undefined =
        orderObj?.id || msg?.orderId || msg?.id || msg?.old?.id || msg?.new?.id;

      // Debug eventType and order info
      console.debug(
        "[orders-realtime] eventType:",
        eventType,
        "orderObj:",
        orderObj,
        "orderId:",
        orderId,
      );

      switch (eventType) {
        case "DELETE": {
          const deletedId = orderId;
          if (!deletedId) {
            console.warn(
              "[orders-realtime] DELETE event with missing orderId",
              msg,
            );
            return;
          }

          const orderIndex = paginatedOrdersCurrentDay.findIndex(
            (order) => order.id === deletedId,
          );

          const currentOrderDetail = store.orderDetail;
          const stateUpdates: any = {};

          if (currentOrderDetail && currentOrderDetail.id === deletedId) {
            stateUpdates.orderDetail = null;
          }

          if (orderIndex !== -1) {
            const updatedOrders = [...paginatedOrdersCurrentDay];
            updatedOrders.splice(orderIndex, 1);
            stateUpdates.paginatedOrdersCurrentDay = updatedOrders;
            stateUpdates.totalCount = Math.max(0, store.totalCount - 1);

            const newTotalPages = Math.ceil(stateUpdates.totalCount / pageSize);
            stateUpdates.totalPages = newTotalPages;

            if (updatedOrders.length === 0 && currentPage > 1) {
              stateUpdates.currentPage = currentPage - 1;
            }
          }

          if (Object.keys(stateUpdates).length > 0) {
            console.debug(
              "[orders-realtime] DELETE stateUpdates applied:",
              stateUpdates,
            );
            useOrdersStore.setState(stateUpdates);
          }
          // Notify listeners
          dispatchRealtimeUpdate({ type: "DELETE", orderId: deletedId });
          break;
        }

        case "INSERT": {
          const id = orderId;
          if (!id && !orderObj) {
            console.warn(
              "[orders-realtime] INSERT event missing id/orderObj:",
              msg,
            );
            return;
          }

          let newOrder: Order | null = null;
          if (orderObj && orderObj.id) {
            if (
              typeof orderObj.branch_id === "number" &&
              orderObj.branch &&
              orderObj.branch.brand_id
            ) {
              newOrder = orderObj as Order;
              console.debug(
                "[orders-realtime] Using orderObj for INSERT:",
                newOrder,
              );
            } else {
              newOrder = await fetchOrder(orderObj.id);
              console.debug(
                "[orders-realtime] Refetched INSERT order:",
                newOrder,
              );
            }
          } else if (id) {
            newOrder = await fetchOrder(id);
            console.debug(
              "[orders-realtime] Refetched INSERT by id:",
              newOrder,
            );
          }

          if (!newOrder) {
            console.warn(
              "[orders-realtime] Could not resolve order for INSERT:",
              msg,
            );
            return;
          }

          const shouldShowOrder = checkOrderMatchesFilters(
            newOrder,
            fetchOptions,
          );

          const currentOrderDetail = store.orderDetail;
          const stateUpdates: any = {};

          stateUpdates.totalCount = store.totalCount + 1;
          const newTotalPages = Math.ceil(stateUpdates.totalCount / pageSize);
          stateUpdates.totalPages = newTotalPages;

          if (shouldShowOrder && currentPage === 1) {
            const updatedOrders = [newOrder, ...paginatedOrdersCurrentDay];
            stateUpdates.paginatedOrdersCurrentDay = updatedOrders.slice(
              0,
              pageSize,
            );

            if (!notifiedOrderIds.has(newOrder.id)) {
              toastHandler({
                title: "Nueva orden recibida",
                description: `Orden #${newOrder.id} - ${newOrder.tracking_id} ha sido creada`,
                variant: "default",
              });
              notifiedOrderIds.add(newOrder.id);
              console.debug(
                "[orders-realtime] Toast sent for order:",
                newOrder.id,
              );
            }
          }

          if (currentOrderDetail && currentOrderDetail.id === newOrder.id) {
            stateUpdates.orderDetail = newOrder;
          }

          console.debug(
            "[orders-realtime] INSERT stateUpdates applied:",
            stateUpdates,
          );

          useOrdersStore.setState(stateUpdates);
          // Notify listeners with new order payload
          dispatchRealtimeUpdate({
            type: "INSERT",
            orderId: newOrder.id,
            order: newOrder,
          });
          break;
        }

        case "UPDATE": {
          const id = orderId;
          if (!id && !orderObj) {
            console.warn(
              "[orders-realtime] UPDATE event missing id/orderObj:",
              msg,
            );
            return;
          }

          let updatedOrder: Order | null = null;
          if (orderObj && orderObj.id) {
            if (
              typeof orderObj.branch_id === "number" &&
              orderObj.branch &&
              orderObj.branch.brand_id
            ) {
              updatedOrder = orderObj as Order;
              console.debug(
                "[orders-realtime] Using orderObj for UPDATE:",
                updatedOrder,
              );
            } else {
              updatedOrder = await fetchOrder(orderObj.id);
              console.debug(
                "[orders-realtime] Refetched UPDATE order:",
                updatedOrder,
              );
            }
          } else if (id) {
            updatedOrder = await fetchOrder(id);
            console.debug(
              "[orders-realtime] Refetched UPDATE by id:",
              updatedOrder,
            );
          }

          if (!updatedOrder) {
            console.warn(
              "[orders-realtime] Could not resolve order for UPDATE:",
              msg,
            );
            return;
          }

          const existingOrderIndex = paginatedOrdersCurrentDay.findIndex(
            (order) => order.id === updatedOrder!.id,
          );

          const currentOrderDetail = store.orderDetail;
          const stateUpdates: any = {};

          const shouldShowOrder = checkOrderMatchesFilters(
            updatedOrder,
            fetchOptions,
          );

          if (existingOrderIndex !== -1) {
            if (shouldShowOrder) {
              const updatedOrders = [...paginatedOrdersCurrentDay];
              updatedOrders[existingOrderIndex] = updatedOrder;
              stateUpdates.paginatedOrdersCurrentDay = updatedOrders;
            } else {
              const updatedOrders = [...paginatedOrdersCurrentDay];
              updatedOrders.splice(existingOrderIndex, 1);
              stateUpdates.paginatedOrdersCurrentDay = updatedOrders;
              stateUpdates.totalCount = Math.max(0, store.totalCount - 1);
              const newTotalPages = Math.ceil(
                stateUpdates.totalCount / pageSize,
              );
              stateUpdates.totalPages = newTotalPages;
            }
          } else if (shouldShowOrder) {
            stateUpdates.totalCount = store.totalCount + 1;
            const newTotalPages = Math.ceil(stateUpdates.totalCount / pageSize);
            stateUpdates.totalPages = newTotalPages;

            if (currentPage === 1) {
              const updatedOrders = [
                updatedOrder,
                ...paginatedOrdersCurrentDay,
              ];
              stateUpdates.paginatedOrdersCurrentDay = updatedOrders.slice(
                0,
                pageSize,
              );
            }
          }

          if (currentOrderDetail && currentOrderDetail.id === updatedOrder.id) {
            stateUpdates.orderDetail = updatedOrder;
          }

          if (Object.keys(stateUpdates).length > 0) {
            console.debug(
              "[orders-realtime] UPDATE stateUpdates applied:",
              stateUpdates,
            );
            useOrdersStore.setState(stateUpdates);
          }
          // Notify listeners with updated order payload
          dispatchRealtimeUpdate({
            type: "UPDATE",
            orderId: updatedOrder.id,
            order: updatedOrder,
          });
          break;
        }

        default: {
          // Unknown message - ignore
          console.debug("[orders-realtime] Unknown WS event, ignoring:", msg);
          return;
        }
      }

      // Always update the counts to reflect status changes
      if (store && typeof store.fetchCurrentDayOrdersCounts === "function") {
        console.debug(
          "[orders-realtime] Fetching current day orders counts after message processing.",
        );
        store.fetchCurrentDayOrdersCounts();
      } else {
        console.warn(
          "[orders-realtime] Store missing fetchCurrentDayOrdersCounts",
          store,
        );
      }
    });

    socket.on("disconnect", (reason: string) => {
      console.warn("[orders-realtime] Socket.IO disconnected", { reason });
      dispatchRealtimeEvent("close");
    });
  } catch (err) {
    console.error("[orders-realtime] Error connecting Socket.IO:", err);
    // Socket.IO has built-in reconnection; no manual schedule here
  }
}

export function cleanupOrdersRealtime() {
  if (socket) {
    try {
      console.debug("[orders-realtime] Closing WebSocket connection...");
      socket.close();
    } catch (e) {
      // noop
      console.warn("[orders-realtime] Exception during socket.close():", e);
    }
  }
  socket = null;
  dispatchRealtimeEvent("close");
}

// Helper function to check if an order matches current filters
function checkOrderMatchesFilters(order: Order, fetchOptions: any): boolean {
  const today = new Date();
  const orderDate = new Date(order.created_at);
  const isToday = orderDate.toDateString() === today.toDateString();

  if (!isToday) return false;

  // Check status filter
  if (fetchOptions?.status && fetchOptions.status !== "all") {
    if (order.status !== fetchOptions.status) return false;
  }

  // Check branch filter
  if (fetchOptions?.branchId) {
    if (order.branch_id !== fetchOptions.branchId) return false;
  }

  // Check brand filter
  if (fetchOptions?.brandId) {
    if (order.branch?.brand_id !== fetchOptions.brandId) return false;
  }

  // Check tracking ID filter
  if (fetchOptions?.trackingId && fetchOptions.trackingId.trim() !== "") {
    if (
      !order.tracking_id
        .toLowerCase()
        .includes(fetchOptions.trackingId.toLowerCase())
    ) {
      return false;
    }
  }

  return true;
}

// Helper function to check if realtime is available (deprecated - use useRealtimeStatus hook instead)
export function checkRealtimeConnection() {
  return isOrdersRealtimeActive();
}
